import { createContext, useRef, useState } from "react";

import { QaProps } from "common/blocks/FAQ/QA";
import useHasMounted from "hooks/useHasMounted/useHasMounted";
import { ResolvedReviewSpecificPageModel } from "models/reviewSpecificPageModel";
import { ResolvedPersonDataStructure } from "resolvers/pages/authorProfileResolver";
import { ResolvedBlogPost } from "resolvers/pages/blogPostResolver";
import { ResolvedReviewSpecificPageContent } from "resolvers/pages/reviewSpecificPageResolver";
import { ResolvedStaticPageContent } from "resolvers/pages/staticPageResolver";
import {
    BlogPosting,
    Graph,
    Person,
    Product,
    ProfilePage,
    Question,
    Vehicle,
    VideoObject,
} from "schema-dts";
import { VehicleService } from "services/vehicle/vehicle";
import { convertHtmlListToStringArray } from "utils/convertHtmlListToStringArray";
import { stripHtmlTags } from "utils/stripHtmlTags";

export type StructuredDataContextProps = {
    createProfilePageStructuredData: (props: ResolvedPersonDataStructure) => void;
    createBlogPostStructuredData: (props: ResolvedBlogPost) => void;
    createFaqStructuredData: (props: QaProps[]) => void;
    createVideoStructuredData: (props: StructuredDataVideoProps) => void;
    createPdpStructuredData: (props: StructuredDataPdpProps) => void;
    createReviewStructuredData: (props: ResolvedReviewSpecificPageModel) => void;
    createStaticPageStructuredData: (props: ResolvedStaticPageContent) => void;
    pdpContent: string | null;
} | null;

export const StructuredDataContext = createContext<StructuredDataContextProps>(null);

export type StructuredDataProviderProps = {
    children: React.ReactNode;
};

export type StructuredDataVideoProps = {
    name: string;
    description: string;
    contentUrl: string;
    uploadDate: string;
    thumbnailUrl: string;
};

export type StructuredDataPdpProps = {
    vehicle: VehicleService.Vehicle;
    formattedMake: string;
    formattedModel: string;
};

/*
    1.  Usage:
        Use the provided functions inside a useEffect hook to create the structured data.
        Failing to use inside a useEffect with result in bad setSate console errors.

        Example:
        ```
            useEffect(() => {
                if (props) createBlogPostStructuredData(props);
            }, [createBlogPostStructuredData, props]);
        ```

    2.  Follow this template for additional structured data functions within this provider:
        ```
            const createSomeTypeOfStructuredData = (props) => {
                if (!hasMounted) return;

                const someTypedSturcturedData: SomeTypeOfStructuredDataFromSchemaDts = {
                    "@type": "someValidSchemeType",
                    ...implementSchemaAccordingToType,
                };

                const someTypeOfStructuredData = JSON.stringify({
                    "@context": "https://schema.org",
                    someTypedSturcturedData,
                });

                setSomeTypeOfContent(someTypeOfStructuredData);
            };
        ```
*/

const StructuredDataProvider = ({ children }: StructuredDataProviderProps) => {
    const hasMounted = useHasMounted();
    const initialFaqQuestionsSetRef = useRef(false);
    const initialVideoSetRef = useRef(false);
    const initialReviewSetRef = useRef(false);
    const [blogPostingContent, setBlogPostingContent] = useState<string | null>(null);
    const [profilePageContent, setProfilePageContent] = useState<string | null>(null);
    const [faqContent, setFaqContent] = useState<string | null>(null);
    const [videoContent, setVideoContent] = useState<string | null>(null);
    const [pdpContent, setPdpContent] = useState<string | null>(null);
    const [reviewContent, setReviewContent] = useState<string | null>(null);
    const [staticPageContent, setStaticPageContent] = useState<string | null>(null);

    const createVideoStructuredData = (props: StructuredDataVideoProps) => {
        if (!hasMounted) return;
        const { current: initialised } = initialVideoSetRef;
        const isFirstRun = !videoContent && !initialised;
        const isNotFirstRun = videoContent && initialised;

        const videoObject: VideoObject = {
            "@type": "VideoObject",
            name: props.name,
            description: props.description,
            contentUrl: props.contentUrl,
            uploadDate: props.uploadDate,
            thumbnailUrl: props.thumbnailUrl,
        };

        if (isFirstRun) {
            setVideoContent(
                JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": [videoObject],
                } as Graph)
            );
            initialVideoSetRef.current = true;
        }
        if (isNotFirstRun) {
            setVideoContent((oldState) => {
                if (!oldState) return null;
                const parsedVideoContent = JSON.parse(oldState);
                const oldGraph = parsedVideoContent["@graph"] || [];
                const nonUniqueGraph = [...oldGraph, videoObject];

                const newGraph = [
                    ...new Map(nonUniqueGraph.map((item) => [item["name"], item])).values(),
                ];

                return JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": newGraph,
                } as Graph);
            });
        }
    };

    const createBlogPostStructuredData = (props: ResolvedBlogPost) => {
        if (!hasMounted) return;
        const absoluteUrl = window.location.origin + props.relativeUrl;
        const blogPosting: BlogPosting = {
            "@type": "BlogPosting",
            headline: props.title,
            timeRequired: `PT${props.readTimeMins}M`,
            url: absoluteUrl,
            image: props.image.src,
            datePublished: props.datePublished,
            dateModified: props.dateModified,
            isAccessibleForFree: true,
            author: {
                "@type": "Person",
                name: props.author.name,
                description: props.author.description || undefined,
                image: props.author.image?.src || "/images/author-fallback.svg",
                url: `${process.env.NEXT_PUBLIC_APP_URL}/${props.author.authorProfileUrl}`,
            },
            articleBody: stripHtmlTags(props.body.html),
        };
        const blogPostStructuredData = JSON.stringify({
            "@context": "https://schema.org",
            ...blogPosting,
        });
        setBlogPostingContent(blogPostStructuredData);
    };

    const createProfilePageStructuredData = (props: ResolvedPersonDataStructure) => {
        if (!hasMounted) return;
        const profilePage: ProfilePage = {
            "@type": "ProfilePage",
            headline: props.metaTitle,
            url: `https://www.motorpoint.co.uk/${props.urlSlug}`,
            "@id": `https://www.motorpoint.co.uk/${props.urlSlug}#profilepage`,
            inLanguage: "en-GB",
            mainEntity: { "@id": `https://www.motorpoint.co.uk/${props.urlSlug}#person` },
            isPartOf: {
                "@type": "WebSite",
                name: "Motorpoint",
                url: "https://www.motorpoint.co.uk/",
                "@id": "https://www.motorpoint.co.uk/#website",
                publisher: {
                    "@type": "Organization",
                    name: "Motorpoint",
                    url: "https://www.motorpoint.co.uk/",
                    "@id": "https://www.motorpoint.co.uk/#organiztaion",
                },
            },
        };
        const person: Person = {
            "@type": "Person",
            name: props.name,
            url: `https://www.motorpoint.co.uk/${props.urlSlug}`,
            "@id": `https://www.motorpoint.co.uk/${props.urlSlug}#person`,
            jobTitle: {
                "@type": "DefinedTerm",
                name: props.jobTitle,
            },
            knowsAbout: props.knowsAbout,
            worksFor: {
                "@id": "https://www.motorpoint.co.uk/#organization",
                url: "https://www.motorpoint.co.uk/",
                name: "Motorpoint",
            },
            sameAs: props.sameAs,
        };
        const profilePageStructuredData = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [profilePage, person],
        } as Graph);
        setProfilePageContent(profilePageStructuredData);
    };

    const createFaqStructuredData = (questions: QaProps[]) => {
        if (!hasMounted) return;
        const { current: initialised } = initialFaqQuestionsSetRef;
        const isFirstRun = !faqContent && !initialised;
        const isNotFirstRun = faqContent && initialised;

        const mapToSchemaQuestions = (questions: QaProps[]): Question[] => {
            return questions.map((props) => ({
                "@type": "Question",
                name: props.question,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: stripHtmlTags(props.answer.html),
                },
            }));
        };

        if (isFirstRun) {
            const faqQuestions: Question[] = mapToSchemaQuestions(questions);
            setFaqContent(
                JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: faqQuestions,
                })
            );
            initialFaqQuestionsSetRef.current = true;
        }

        if (isNotFirstRun) {
            const parsedFaqContent = JSON.parse(faqContent);
            const oldMainEntity: Question[] = parsedFaqContent.mainEntity || [];
            const nonUniqueMainEntity: Question[] = [
                ...oldMainEntity,
                ...mapToSchemaQuestions(questions),
            ];

            const mainEntity = [
                ...new Map(nonUniqueMainEntity.map((item) => [item["name"], item])).values(),
            ];

            setFaqContent(
                JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity,
                })
            );
        }
    };

    const createPdpStructuredData = (props: StructuredDataPdpProps) => {
        if (!hasMounted) return;

        const { vehicle } = props;

        const basicCommercialPrice = vehicle.CurrentPrice.SalePrice.BasicPrice;
        const basicNonCommercialPrice =
            vehicle.CurrentPrice.SalePrice.BasicPrice +
            (vehicle.CurrentPrice.SalePrice.VATAmount ?? 0);

        const price = !!vehicle?.Taxonomy?.IsCommercial
            ? basicCommercialPrice
            : basicNonCommercialPrice;

        const priceValidUntil = new Date();
        priceValidUntil.setDate(
            priceValidUntil.getDate() + ((1 + 7 - priceValidUntil.getDay()) % 7 || 7)
        );

        let driveWheelConfig: null | string = null;
        const driveTrain = vehicle.DriveTrain.toLowerCase();

        switch (true) {
            case driveTrain.includes("front"): {
                driveWheelConfig = "Front";
                break;
            }
            case driveTrain.includes("rear"): {
                driveWheelConfig = "Rear";
                break;
            }
            case driveTrain.includes("all"): {
                driveWheelConfig = "All";
                break;
            }
            case driveTrain.includes("4"):
            case driveTrain.includes("four"): {
                driveWheelConfig = "Four";
                break;
            }

            default: {
                driveWheelConfig = null;
            }
        }

        let json: string | undefined = "undefined";

        try {
            json = JSON.stringify({
                "@context": "https://schema.org/",
                "@type": "Vehicle",
                ...(vehicle.CAPTechnicalData?.["Vehicle Dimensions"]?.["Gross Vehicle Weight"] && {
                    weightTotal: {
                        "@type": "QuantitativeValue",
                        maxValue:
                            vehicle.CAPTechnicalData?.["Vehicle Dimensions"]?.[
                                "Gross Vehicle Weight"
                            ].toString(),
                        unitCode: "KGM",
                    },
                }),
                ...(vehicle.CAPTechnicalData?.["Vehicle Dimensions"]?.["Wheelbase"] && {
                    wheelbase: {
                        "@type": "QuantitativeValue",
                        value: vehicle.CAPTechnicalData?.["Vehicle Dimensions"]?.[
                            "Wheelbase"
                        ].toString(),
                        unitCode: "MMT",
                    },
                }),
                sku: vehicle.StockBookNumber.toString(),
                mileageFromOdometer: {
                    "@type": "QuantitativeValue",
                    value: vehicle.Mileage.toString(),
                    unitCode: "SMI",
                },
                ...(!!driveWheelConfig && {
                    driveWheelConfiguration: `https://schema.org/${driveWheelConfig}WheelDriveConfiguration`,
                }),
                dateVehicleFirstRegistered: vehicle.DateOfFirstUKRegistration,
                steeringPosition: "https://schema.org/RightHandDriving",
                numberOfAxles: "2",
                ...(vehicle.DateOfManufactured && { productionDate: vehicle.DateOfManufactured }),
                ...(vehicle?.CAPTechnicalData.Performance?.["Gears"] && {
                    numberOfForwardGears:
                        parseInt(`${vehicle?.CAPTechnicalData.Performance?.["Gears"]}` ?? "6", 10) -
                        1,
                }),
                offers: {
                    "@type": "Offer",
                    warranty: {
                        "@type": "WarrantyPromise",
                        "@id": "motorpoint-warrenty-promise",
                        name: "Extended warranty",
                        url: "https://www.motorpoint.co.uk/extras/warranty",
                        description:
                            "Almost all of our cars are still under their manufacturer’s warranty, so peace of mind comes as standard with Motorpoint. For added reassurance, you can go even further with our extended warranty.",
                    },
                    offeredBy: {
                        "@type": "AutoDealer",
                        brand: {
                            "@type": "Corporation",
                            name: "Motorpoint Ltd",
                            url: "https://www.motorpoint.co.uk/",
                            slogan: "Car Buying Made Easy",
                        },
                    },
                    ...(vehicle.AllocatedLocation?.Address && {
                        seller: {
                            "@type": "AutoDealer",
                            address: {
                                "@type": "PostalAddress",
                                streetAddress: `${vehicle.AllocatedLocation.Address.Line1}, ${vehicle.AllocatedLocation.Address.Line2}, ${vehicle.AllocatedLocation.Address.Line3}`,
                                postalCode: vehicle.AllocatedLocation.Address.Postcode,
                                addressLocality: vehicle.AllocatedLocation.Address.Locality,
                            },
                            name: `Motorpoint ${vehicle.AllocatedLocation.Name}`,
                        },
                    }),
                    shippingDetails: [
                        {
                            "@type": "OfferShippingDetails",
                            "@id": "collection",
                            name: "Reserve and Collect",
                            description: "Collect the vehicle from a Motorpoint store",
                            url: "https://www.motorpoint.co.uk/why-choose-motorpoint/reserve-and-collect",
                        },
                        {
                            "@type": "OfferShippingDetails",
                            "@id": "home-delivery",
                            name: "Home Delivery",
                            url: "https://www.motorpoint.co.uk/why-choose-motorpoint/home-delivery",
                            description: "The vehicle is delivered to your home address",
                        },
                    ],
                    hasMerchantReturnPolicy: {
                        "@type": "MerchantReturnPolicy",
                        name: "7 day exchange guarantee",
                        url: "https://www.motorpoint.co.uk/why-choose-motorpoint/7-day-exchange-guarantee",
                        description:
                            "Every car we sell from our stores is covered by our 7 day exchange guarantee. This means that you can exchange your car for another one if you're not happy with it – giving you extra peace of mind.",
                    },
                    price: Math.floor(price),
                    priceCurrency: "GBP",
                    availability: "https://schema.org/InStock",
                    url: window.location.href.split("?")[0],
                    priceValidUntil: priceValidUntil.toISOString(),
                    availabilityStarts: new Date().toISOString(),
                    ...(vehicle.AllocatedLocation?.Address && {
                        seller: {
                            "@type": "AutoDealer",
                            address: {
                                "@type": "PostalAddress",
                                streetAddress: `${vehicle.AllocatedLocation.Address.Line1}, ${vehicle.AllocatedLocation.Address.Line2}, ${vehicle.AllocatedLocation.Address.Line3}`,
                                postalCode: vehicle.AllocatedLocation.Address.Postcode,
                                addressLocality: vehicle.AllocatedLocation.Address.Locality,
                            },
                            name: `Motorpoint ${vehicle.AllocatedLocation.Name}`,
                        },
                    }),
                },
                modelDate: vehicle.DateOfManufactured,
                description:
                    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.content ??
                    "",
                ...(vehicle?.CAPTechnicalData?.Performance?.["Top Speed"] && {
                    speed: {
                        "@type": "QuantitativeValue",
                        maxValue: vehicle.CAPTechnicalData.Performance["Top Speed"],
                        unitCode: "MPH",
                    },
                }),
                image: [
                    ...vehicle.Imagery.MarketingImages.map((image) => image.Uri),
                    ...vehicle.Imagery.OperationalImages.map((image) => image.Uri),
                ],
                vehicleTransmission: vehicle.Transmission,
                bodyType: vehicle.BodyType,
                seatingCapacity: vehicle.StandardEquipment["No. of Seats"],
                vehicleEngine: [
                    {
                        "@type": "EngineSpecification",
                        fuelType: vehicle.FuelType,
                        ...(vehicle?.CAPTechnicalData?.Performance?.["Engine Power - BHP"] && {
                            enginePower: {
                                "@type": "QuantitativeValue",
                                value: vehicle.CAPTechnicalData.Performance["Engine Power - BHP"],
                                unitCode: "BHP",
                            },
                        }),
                        ...(!!vehicle?.CAPTechnicalData?.Performance?.["Engine Torque - NM"] && {
                            torque: {
                                "@type": "QuantitativeValue",
                                value: vehicle.CAPTechnicalData.Performance["Engine Torque - NM"],
                                unitCode: "F17",
                            },
                        }),
                    },
                ],
                numberOfDoors: vehicle.DoorQuantity,
                ...(vehicle?.CAPTechnicalData?.["Emissions - ICE"]?.["CO2 (g/km)"] && {
                    emissionsCO2: vehicle.CAPTechnicalData["Emissions - ICE"]["CO2 (g/km)"],
                }),
                ...(vehicle?.CAPTechnicalData?.Performance?.["0 to 62 mph (secs)"] && {
                    accelerationTime: {
                        "@type": "QuantitativeValue",
                        value: vehicle.CAPTechnicalData.Performance["0 to 62 mph (secs)"],
                        unitCode: "SEC",
                        valueReference: {
                            "@type": "QuantitativeValue",
                            unitText: "0..62 mph",
                        },
                    },
                }),
                name: `${props.formattedMake} ${props.formattedModel}`,
                manufacturer: props.formattedMake,
                vehicleConfiguration: vehicle.Taxonomy.Trim,
                vehicleIdentificationNumber: vehicle.StockBookNumber.toString(),
                color: vehicle.Colour?.ManufacturerName ?? vehicle.Colour?.Name,
            } as Vehicle);
            setPdpContent(json);
        } catch (error) {
            console.error("Could not parse structured data for vehicle.");
        }
    };

    const createReviewStructuredData = (props: ResolvedReviewSpecificPageModel) => {
        if (!hasMounted) return;
        const { current: initialised } = initialReviewSetRef;
        const isFirstRun = !reviewContent && !initialised;
        const isNotFirstRun = reviewContent && initialised;
        // convert pros and cons to array of strings then, clean of html tags
        const listOfPros = props.page.reviewIntro.pros
            ? convertHtmlListToStringArray(props.page.reviewIntro.pros?.html)
            : [];
        const listOfCons = props.page.reviewIntro.cons
            ? convertHtmlListToStringArray(props.page.reviewIntro.cons?.html)
            : [];

        const rating = props.page.reviewIntro.rating ?? "";

        const author: Person = {
            "@type": "Person",
            name: props.page.reviewIntro.author.name,
            description: props.page.reviewIntro.author.description || undefined,
            image: props.page.reviewIntro.author.image?.src || "/images/author-fallback.svg",
            url: `${process.env.NEXT_PUBLIC_APP_URL}/${props.page.reviewIntro.author.authorProfileUrl}`,
        };

        const product: Product = {
            "@type": "Product",
            name: props.page.reviewIntro.makeModel,
            description: stripHtmlTags(props.page.reviewIntro.overview.html),
            review: {
                "@type": "Review",
                name: props.page.reviewIntro.title,
                dateModified: props.page.reviewIntro?.dateModified || undefined,
                datePublished: props.page.reviewIntro?.datePublished || undefined,
                reviewRating: {
                    "@type": "Rating",
                    ratingValue: rating,
                    bestRating: 10,
                },
                author: {
                    "@type": "Person",
                    name: props.page.reviewIntro.author.name,
                },
                ...(listOfPros && {
                    positiveNotes: {
                        "@type": "ItemList",
                        itemListElement: [
                            {
                                "@type": "ListItem",
                                position: 1,
                                name: listOfPros[0].trim(),
                            },
                            {
                                "@type": "ListItem",
                                position: 2,
                                name: listOfPros[1].trim(),
                            },
                            {
                                "@type": "ListItem",
                                position: 3,
                                name: listOfPros[2].trim(),
                            },
                        ],
                    },
                }),
                ...(listOfCons && {
                    negativeNotes: {
                        "@type": "ItemList",
                        itemListElement: [
                            {
                                "@type": "ListItem",
                                position: 1,
                                name: listOfCons[0].trim(),
                            },
                            {
                                "@type": "ListItem",
                                position: 2,
                                name: listOfCons[1].trim(),
                            },
                            {
                                "@type": "ListItem",
                                position: 3,
                                name: listOfCons[2].trim(),
                            },
                        ],
                    },
                }),
                ...(props.page.cheapestVehicle?.CurrentPrice && {
                    offers: {
                        "@type": "AggregateOffer",
                        priceCurrency: "GBP",
                        lowPrice: props.page.cheapestVehicle?.CurrentPrice.toPrecision(2),
                    },
                }),
                isAccessibleForFree: true,
            },
        };
        let graph: (Person | Product | VideoObject)[] = [author, product];

        if (videoContent) {
            graph.push(JSON.parse(videoContent));
        }

        if (isFirstRun) {
            setReviewContent(
                JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": graph,
                } as Graph)
            );
            initialReviewSetRef.current = true;
        }
        if (isNotFirstRun) {
            setReviewContent((oldState) => {
                if (!oldState) return null;
                const parsedReviewContent = JSON.parse(oldState);
                const oldGraph = parsedReviewContent["@graph"] || [];
                const nonUniqueGraph = [...oldGraph, graph];

                const newGraph = [
                    ...new Map(nonUniqueGraph.map((item) => [item["name"], item])).values(),
                ];

                return JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": newGraph,
                } as Graph);
            });
        }
        const reviewStructuredData = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": graph,
        });

        setReviewContent(reviewStructuredData);
    };

    const createStaticPageStructuredData = (props: ResolvedStaticPageContent) => {
        if (!hasMounted) return;
        setStaticPageContent(JSON.stringify(JSON.parse(props.structuredDataSchema)));
    };

    return (
        <>
            <StructuredDataContext.Provider
                value={{
                    createBlogPostStructuredData,
                    createProfilePageStructuredData,
                    createFaqStructuredData,
                    createVideoStructuredData,
                    createPdpStructuredData,
                    createReviewStructuredData,
                    createStaticPageStructuredData,
                    pdpContent,
                }}
            >
                {children}
            </StructuredDataContext.Provider>
            {!!blogPostingContent && (
                <script id="structuredDataBlogPosting" type="application/ld+json">
                    {blogPostingContent}
                </script>
            )}
            {!!faqContent && (
                <script id="structuredDataFaq" type="application/ld+json">
                    {faqContent}
                </script>
            )}
            {!!videoContent && !reviewContent && (
                <script id="structuredDataVideo" type="application/ld+json">
                    {videoContent}
                </script>
            )}
            {!!pdpContent && (
                <script id="structuredDataPdp" type="application/ld+json">
                    {pdpContent}
                </script>
            )}
            {!!profilePageContent && (
                <script id="structuredDataPerson" type="application/ld+json">
                    {profilePageContent}
                </script>
            )}
            {!!reviewContent && (
                <script id="structuredDataReview" type="application/ld+json">
                    {reviewContent}
                </script>
            )}
            {!!staticPageContent && (
                <script id="structuredDataStatic" type="application/ld+json">
                    {staticPageContent}
                </script>
            )}
        </>
    );
};

export default StructuredDataProvider;
